import {
  Calendar,
  ChevronRight,
  FileMinus,
  LibraryBig,
  SquareArrowOutUpRight,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { getProjectSites } from "../../../Services/projectServices";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { useProjects } from "../../Project/Hooks/useProjects";
import { BILLS_LIST } from "../../../routes";
import { useDashboard } from "../Hooks/useDashboard";
import { formatAmountINR } from "../Constants";
import { getCategoryColor } from ".";
import { TOAST_MESSAGES } from "../Constants";

const flattenProjectData = (projectData) => {
  const flattened = {};
  Object.entries(projectData).forEach(([key, value]) => {
    if (typeof value === "object" && value.amount !== undefined && value.type) {
      flattened[key] = value.amount;
      flattened[`${key}_type`] = value.type; // store type for tooltip if needed
    }
  });
  return flattened;
};

const BillDonutChart = ({ projectData, loading }) => {
  if (loading) {
    return <div className="w-full h-96 bg-gray-200 animate-pulse rounded-lg" />;
  }

  const flattenedData = flattenProjectData(projectData);

  const chartData = Object.keys(flattenedData)
    .filter((key) => !key.endsWith("_type"))
    .map((key) => ({
      name: key,
      value: flattenedData[key],
      type: flattenedData[`${key}_type`],
    }));

  const total = chartData.reduce((sum, item) => sum + item.value, 0);
  const allZero = total === 0;
  const displayData = allZero ? [{ name: "No Data", value: 1 }] : chartData;

  return (
    <div className="relative w-50 h-50 flex items-center justify-center">
      {/* <ResponsiveContainer width="100%" height="100%"> */}
        <PieChart width={160} height={160}>
          <Pie
            data={displayData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={70}
            dataKey="value"
            nameKey="name"
          >
            {displayData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  allZero
                    ? "#e5e7eb"
                    : getCategoryColor(entry.name) || "#6b7280"
                }
              />
            ))}
          </Pie>
          {!allZero && (
            <Tooltip
              formatter={(value, name, props) => {
                const total = displayData.reduce(
                  (sum, item) => sum + item.value,
                  0
                );
                const percentage =
                  total > 0 ? ((value / total) * 100).toFixed(2) : 0;
                const type = props.payload.type || "";
                return [
                  `${formatAmountINR(value)} (${percentage}%)`,
                  `${name} ${type ? `(${type})` : ""}`,
                ];
              }}
            />
          )}
          {!allZero && <Legend verticalAlign="top" />}
        </PieChart>
      {/* </ResponsiveContainer> */}

      <div
        className={`absolute inset-0 flex items-center justify-center pointer-events-none ${
          !allZero ? "translate-y-4" : ""
        }`}
      >
        <p className="text-lg font-bold text-gray-800">
          {allZero ? "₹0" : formatAmountINR(total)}
        </p>
      </div>
    </div>
  );
};

const BillingExplorer = ({ filter }) => {
  const [projectDetails, setProjectDetails] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [projectSites, setProjectSites] = useState(null);

  const {
    projects,
    fetchProjects,
    setFilters,
    filters,
    loading: loadingProjects,
  } = useProjects();
  const { fetchBillSummaryByProjectId, fetchBillSummaryBySiteId } =
    useDashboard();
  const navigate = useNavigate();

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setSelectedSite(null);
    fetchData();
  };

  const handleSiteClick = async (siteId) => {
    setSelectedSite(siteId);
    setLoadingDetails(true);
    try {
      const res = await fetchBillSummaryBySiteId(siteId);
      setProjectDetails(res); // reuse same state
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchData = async () => {
    if (!selectedProject) return;
    setLoadingDetails(true);
    try {
      const res = await fetchBillSummaryByProjectId(selectedProject, {
        limit: 0,
      });
      setProjectDetails(res);
    } finally {
      setLoadingDetails(false);
    }
  };

  const renderSiteList = () => {
    if (loadingSites) {
      return new Array(3)
        .fill(0)
        .map((_, idx) => (
          <div
            key={idx}
            className="h-10 bg-gray-200 animate-pulse rounded mb-2"
          />
        ));
    }

    if (!projectSites || projectSites.length === 0) {
      return (
        <div className="flex flex-col justify-center items-center h-full text-gray-400">
          <LibraryBig className="text-gray-300 mb-2" />
          <p>No Sites for project...</p>
        </div>
      );
    }

    return projectSites.map((site) => (
      <li
        key={site.id}
        className={`flex items-center justify-between gap-2 mb-1 cursor-pointer p-2 font-medium rounded transition-colors ${
          selectedSite === site.id
            ? "bg-blue-100 text-blue-700"
            : "hover:bg-gray-100"
        }`}
        onClick={() => handleSiteClick(site.id)}
      >
        <Calendar size={16} />
        <span className="flex-1 truncate">{site.site_name}</span>
        <ChevronRight size={14} className="text-gray-400" />
      </li>
    ));
  };

  const fetchSites = async () => {
    setLoadingSites(true);
    try {
      const res = await getProjectSites(selectedProject, {
        limit: 0,
        sites: "all",
      });
      if (res.status === 200) {
        setProjectSites(res.data);
      } else {
        toast.error(TOAST_MESSAGES.FETCH_COMPLETION_FAILED);
      }
    } catch {
      toast.error(TOAST_MESSAGES.FETCH_COMPLETION_FAILED);
    } finally {
      setLoadingSites(false);
    }
  };

  useEffect(() => {
    if (selectedProject) {
      fetchData();
      fetchSites();
    }
  }, [selectedProject]);

  useEffect(() => {
    setFilters({ project: filter, status: "IN_PROGRESS" });
    setSelectedProject(null);
  }, [filter]);

  useEffect(() => {
    setSelectedProject(projects[0]?.id);
  }, [projects]);

  useEffect(() => {
    fetchProjects();
  }, [filters]);

  const renderDetails = () => {
    if (loadingDetails) {
      return (
        <div className="w-full h-96 bg-gray-200 animate-pulse rounded-lg" />
      );
    }

    if (projectDetails) {
      return (
        <div className="bg-white space-y-4">
          {" "}
          <div className="flex justify-between items-start">
            {" "}
            <h3 className="font-bold text-gray-800">
              {selectedSite ? "Site Details" : "Project Details"}
            </h3>
            <button
              onClick={() =>
                navigate(BILLS_LIST, {
                  state: {
                    projectId: projectDetails.project_id,
                    projectName: projectDetails.project,
                  },
                })
              }
              className="text-blue-600 flex items-center gap-1 hover:underline"
              title="View Details"
            >
              {" "}
              <SquareArrowOutUpRight size={16} />{" "}
            </button>{" "}
          </div>{" "}
          <div className="flex justify-center">
            {" "}
            <BillDonutChart projectData={projectDetails} />{" "}
          </div>{" "}
          <div className="flex justify-between items-start">
            {" "}
            <p>
              <span className="font-semibold">
                {selectedSite ? "Site:" : "Project:"}
              </span>{" "}
              {projectDetails[selectedSite ? "site" : "project"]}
            </p>
          </div>{" "}
        </div>
      );
    }

    return (
      <div className="flex flex-col justify-center items-center h-full w-full text-gray-400">
        <div className="bg-gray-100 p-2 rounded-full">
          <LibraryBig className="text-gray-300" />
        </div>
        <p className="text-gray-400">Select a project to view details</p>
      </div>
    );
  };

  const renderProjectList = () => {
    if (loadingProjects) {
      return new Array(5)
        .fill(0)
        .map((_, idx) => (
          <div
            key={idx}
            className="h-10 bg-gray-200 animate-pulse rounded mb-2"
          />
        ));
    }

    return projects?.map((project) => (
      <li
        key={project.id}
        className={`flex items-center justify-between gap-2 mb-1 cursor-pointer p-2 font-medium rounded transition-colors ${
          selectedProject === project.id
            ? "bg-blue-100 text-blue-700"
            : "hover:bg-gray-100"
        }`}
        onClick={() => handleProjectClick(project.id)}
      >
        <FileMinus size={16} />
        <span className="flex-1 truncate">{project.name}</span>
        <ChevronRight size={14} className="text-gray-400" />
      </li>
    ));
  };

  return (
    <div className="w-full">
      <div className="flex min-h-full overflow-y-auto w-full">
        {/* Project List */}
        <motion.div
          animate={{ width: selectedSite ? "24%" : "30%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="border-r border-gray-200 flex flex-col w-1/2 p-2"
        >
          <h2 className="font-bold p-2">Projects</h2>
          <div className="flex-1 overflow-y-auto max-h-[calc(100vh-150px)]">
            <ul>{renderProjectList()}</ul>
          </div>
        </motion.div>

        {/* Sites */}
        <motion.div
          animate={{ width: selectedSite ? "26%" : "30%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="border-r border-gray-200 flex flex-col p-2"
        >
          <h2 className="font-bold p-2">Sites</h2>
          <div className="flex-1 overflow-y-auto max-h-[calc(100vh-150px)]">
            <ul>{renderSiteList()}</ul>
          </div>
        </motion.div>

        {/* Details */}
        <motion.div
          animate={{ width: selectedSite ? "55%" : "40%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="p-2 "
        >
          {renderDetails()}
        </motion.div>
      </div>
    </div>
  );
};

export default BillingExplorer;
